import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useMotionValue, useTransform, PanInfo } from 'framer-motion';
import { Bell, ShareNetwork, UserPlus, BellSimple, Archive, Star, X, ArrowCounterClockwise } from '@phosphor-icons/react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { SusSurveyModal } from '@/components/dashboard/SusSurveyModal';
import Tippy from '@tippyjs/react';
import { animateFill } from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/dist/backdrop.css';
import 'tippy.js/animations/shift-away.css';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

type TabValue = 'inbox' | 'archived';

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  image_url?: string | null;
  link: string | null;
  read: boolean;
  created_at: string;
  archived_at: string | null;
  source?: 'user' | 'global';
  global_notification_id?: string;
}

interface UserNotificationRow {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  read: boolean;
  created_at: string;
  archived_at: string | null;
}

interface GlobalNotificationRow {
  id: string;
  title: string;
  content: string;
  type: string;
  created_at: string;
  is_active: boolean;
  image_url: string | null;
  link: string | null;
}

interface UserGlobalActionRow {
  global_notification_id: string;
  read: boolean;
  archived_at: string | null;
}


function is_not_null<T>(value: T | null): value is T {
  return value !== null;
}

function time_ago(iso: string): string {
  return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: ptBR });
}

/* icone por tipo de notificacao */
function NotificationIcon({ type }: { type: string }) {
  const cls = 'h-4 w-4';
  switch (type) {
    case 'share': return <ShareNetwork className={cls} weight="fill" />;
    case 'follow': return <UserPlus className={cls} weight="fill" />;
    case 'sus_survey': return <Star className={cls} weight="fill" />;
    default: return <BellSimple className={cls} weight="fill" />;
  }
}

/* card com swipe para arquivar */
const SWIPE_THRESHOLD = 100;

function SwipeableNotificationCard({
  n,
  on_click,
  on_dismiss,
  on_archive,
}: {
  n: Notification;
  on_click: () => void;
  on_dismiss: () => void;
  on_archive: () => void;
}) {
  const x = useMotionValue(0);
  const bg_opacity = useTransform(x, [-SWIPE_THRESHOLD, 0], [1, 0]);
  const card_opacity = useTransform(x, [-SWIPE_THRESHOLD * 1.5, -SWIPE_THRESHOLD], [0, 1]);

  const handle_drag_end = (_: unknown, info: PanInfo) => {
    if (info.offset.x < -SWIPE_THRESHOLD) {
      on_archive();
    } else if (!n.read && info.offset.x < -SWIPE_THRESHOLD * 0.5) {
      on_dismiss();
    }
  };

  return (
    <div className="relative overflow-hidden rounded-xl group">
      {/* Fundo revelado ao deslizar */}
      <motion.div
        className="absolute inset-0 rounded-xl bg-muted/60 flex items-center justify-end pr-4 gap-1.5"
        style={{ opacity: bg_opacity }}
      >
        <Archive className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">Arquivar</span>
      </motion.div>

      <motion.div
        style={{ x, opacity: card_opacity }}
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.3}
        onDragEnd={handle_drag_end}
        className={cn(
          'relative flex gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-accent/60 w-full text-left',
          !n.read && 'bg-accent/30'
        )}
      >
        {/* Icone do tipo */}
        <button
          onClick={on_click}
          className="mt-0.5 shrink-0 flex h-7 w-7 items-center justify-center rounded-lg cursor-pointer"
          style={{ background: 'transparent' }}
        >
          <span className={cn(
            'flex h-7 w-7 items-center justify-center rounded-lg',
            !n.read ? 'bg-primary/10 text-primary' : 'bg-accent text-muted-foreground'
          )}>
            <NotificationIcon type={n.type} />
          </span>
        </button>

        <button onClick={on_click} className="min-w-0 flex-1 text-left cursor-pointer">
          <div className="flex items-start gap-2">
            {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary animate-pulse" />}
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground leading-tight">{n.title}</p>
              {n.body && <p className="text-xs text-muted-foreground/80 mt-0.5 line-clamp-2">{n.body}</p>}
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground/60 mt-1">{time_ago(n.created_at)}</p>
        </button>

        {/* Botao visivel de arquivar (desktop) */}
        <button
          onClick={(e) => { e.stopPropagation(); on_archive(); }}
          className="shrink-0 self-center opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground"
          aria-label="Arquivar notificacao"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </motion.div>
    </div>
  );
}

/* componente principal */
export function NotificationCenter({
  controlled_open,
  on_controlled_change,
  hidden_trigger,
}: {
  controlled_open?: boolean;
  on_controlled_change?: (v: boolean) => void;
  hidden_trigger?: boolean;
} = {}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [tab, set_tab] = useState<TabValue>('inbox');
  const [internal_open, set_internal_open] = useState(false);

  const open = controlled_open ?? internal_open;
  const set_open = (v: boolean) => {
    set_internal_open(v);
    on_controlled_change?.(v);
  };

  const [notifications, set_notifications] = useState<Notification[]>([]);
  const [archived_notifications, set_archived_notifications] = useState<Notification[]>([]);
  const [loading_inbox, set_loading_inbox] = useState(true);
  const [loading_archived, set_loading_archived] = useState(false);
  const [show_sus_survey, set_show_sus_survey] = useState(false);
  const [detail_notification, set_detail_notification] = useState<Notification | null>(null);

  const unread_count = notifications.filter((n) => !n.read).length;

  const fetch_notifications = useCallback(async () => {
    if (!user) return;
    set_loading_inbox(true);

    const [user_notifications_result, global_notifications_result, global_actions_result] = await Promise.all([
      supabase
        .from('notifications')
        .select('id, type, title, body, link, read, created_at, archived_at')
        .eq('user_id', user.id)
        .is('archived_at', null)
        .order('created_at', { ascending: false })
        .limit(50),
      supabase
        .from('global_notifications')
        .select('id, title, content, type, created_at, is_active, image_url, link')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(50),
      supabase
        .from('user_global_actions')
        .select('global_notification_id, read, archived_at')
        .eq('user_id', user.id),
    ]);

    const user_notifications = ((user_notifications_result.data ?? []) as UserNotificationRow[]).map((item) => ({
      ...item,
      source: 'user' as const,
    }));

    const global_actions_map = new Map(
      ((global_actions_result.data ?? []) as UserGlobalActionRow[]).map((item) => [item.global_notification_id, item]),
    );

    const global_notifications = ((global_notifications_result.data ?? []) as GlobalNotificationRow[])
      .map((item) => {
        const action = global_actions_map.get(item.id);
        return {
          id: `global-${item.id}`,
          type: item.type,
          title: item.title,
          body: item.content,
          image_url: item.image_url,
          link: item.link ?? null,
          read: action?.read ?? false,
          created_at: item.created_at,
          archived_at: action?.archived_at ?? null,
          source: 'global' as const,
          global_notification_id: item.id,
        } satisfies Notification;
      })
      .filter((item) => item.archived_at === null);

    const merged_notifications = [...user_notifications, ...global_notifications]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    set_notifications(merged_notifications);
    set_loading_inbox(false);
  }, [user]);


  const fetch_archived = useCallback(async () => {
    if (!user) return;
    set_loading_archived(true);

    const [user_archived_result, global_notifications_result, global_actions_result] = await Promise.all([
      supabase
        .from('notifications')
        .select('id, type, title, body, link, read, created_at, archived_at')
        .eq('user_id', user.id)
        .not('archived_at', 'is', null)
        .order('archived_at', { ascending: false })
        .limit(50),
      supabase
        .from('global_notifications')
        .select('id, title, content, type, created_at, is_active, image_url, link')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(50),
      supabase
        .from('user_global_actions')
        .select('global_notification_id, read, archived_at')
        .eq('user_id', user.id)
        .not('archived_at', 'is', null),
    ]);

    const user_archived = ((user_archived_result.data ?? []) as UserNotificationRow[]).map((item) => ({
      ...item,
      source: 'user' as const,
    }));

    const global_actions_map = new Map(
      ((global_actions_result.data ?? []) as UserGlobalActionRow[]).map((item) => [item.global_notification_id, item]),
    );

    const global_archived = ((global_notifications_result.data ?? []) as GlobalNotificationRow[])
      .map((item) => {
        const action = global_actions_map.get(item.id);
        if (!action?.archived_at) return null;
        return {
          id: `global-${item.id}`,
          type: item.type,
          title: item.title,
          body: item.content,
          image_url: item.image_url,
          link: item.link ?? null,
          read: action.read,
          created_at: item.created_at,
          archived_at: action.archived_at,
          source: 'global' as const,
          global_notification_id: item.id,
        } satisfies Notification;
      })
      .filter(is_not_null)
      .sort((a, b) => new Date(b.archived_at ?? b.created_at).getTime() - new Date(a.archived_at ?? a.created_at).getTime());

    set_archived_notifications(
      [...user_archived, ...global_archived].sort(
        (a, b) => new Date(b.archived_at ?? b.created_at).getTime() - new Date(a.archived_at ?? a.created_at).getTime(),
      ),
    );
    set_loading_archived(false);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    void fetch_notifications();
  }, [user, fetch_notifications]);

  useEffect(() => {
    if (!open || unread_count === 0) return;
    void mark_all_read();
  }, [open]);

  // Realtime para notificacoes e novidades globais
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`notifications-realtime-${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        () => {
          void fetch_notifications();
          if (tab === 'archived') void fetch_archived();
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'global_notifications' },
        () => {
          void fetch_notifications();
          if (tab === 'archived') void fetch_archived();
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'user_global_actions', filter: `user_id=eq.${user.id}` },
        () => {
          void fetch_notifications();
          if (tab === 'archived') void fetch_archived();
        }
      )
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [user, fetch_notifications, fetch_archived, tab]);

  // Deteccao de pesquisa SUS no 2o dia: insere notificacao real no banco
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data: profile_check } = await supabase
        .from('profiles')
        .select('created_at, sus_dismissed')
        .eq('id', user.id)
        .single();
      if (!profile_check || cancelled) return;
      if ((profile_check as Record<string, unknown>).sus_dismissed) return;

      const diff_days = Math.floor((Date.now() - new Date(profile_check.created_at).getTime()) / 86_400_000);
      if (diff_days < 2) return;

      // verificar se ja completou a pesquisa
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: existing_survey } = await (supabase.from('sus_surveys') as any)
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      if (existing_survey || cancelled) {
        if (existing_survey) {
          void supabase.from('profiles').update({ sus_dismissed: true } as Record<string, unknown>).eq('id', user.id);
        }
        return;
      }

      // verificar se ja existe notificacao sus_survey nao arquivada
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: existing_notif } = await (supabase.from('notifications') as any)
        .select('id')
        .eq('user_id', user.id)
        .eq('type', 'sus_survey')
        .is('archived_at', null)
        .maybeSingle();
      if (existing_notif || cancelled) return;

      // verificar se existe notificacao sus_survey arquivada (usuario ja descartou)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: archived_notif } = await (supabase.from('notifications') as any)
        .select('id')
        .eq('user_id', user.id)
        .eq('type', 'sus_survey')
        .not('archived_at', 'is', null)
        .maybeSingle();
      if (archived_notif || cancelled) {
        // ja descartou antes, marcar sus_dismissed
        if (archived_notif) {
          void supabase.from('profiles').update({ sus_dismissed: true } as Record<string, unknown>).eq('id', user.id);
        }
        return;
      }

      // inserir notificacao real no banco via RPC segura
      if (!cancelled) {
        await supabase.rpc('insert_sus_notification' as never);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const upsert_global_action = async (global_notification_id: string, payload: Partial<UserGlobalActionRow>) => {
    if (!user) return;
    await supabase.from('user_global_actions').upsert({
      user_id: user.id,
      global_notification_id,
      read: payload.read ?? false,
      archived_at: payload.archived_at ?? null,
    }, { onConflict: 'user_id,global_notification_id' });
  };

  const mark_read = async (id: string) => {
    const target = notifications.find((n) => n.id === id);
    if (!target) return;

    if (target.source === 'global' && target.global_notification_id) {
      await upsert_global_action(target.global_notification_id, { read: true, archived_at: target.archived_at ?? null });
    } else {
      await supabase.from('notifications').update({ read: true }).eq('id', id);
    }

    set_notifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
  };

  const archive_notification = async (id: string) => {
    const target = notifications.find((n) => n.id === id);
    if (!target) return;

    const archived_at = new Date().toISOString();

    if (target.source === 'global' && target.global_notification_id) {
      await upsert_global_action(target.global_notification_id, { read: true, archived_at });
    } else {
      await supabase
        .from('notifications')
        .update({ archived_at })
        .eq('id', id);
    }

    set_notifications((prev) => prev.filter((n) => n.id !== id));

    if (target.type === 'sus_survey' && user) {
      void supabase.from('profiles').update({ sus_dismissed: true } as Record<string, unknown>).eq('id', user.id);
    }
  };

  const archive_all_notifications = async () => {
    if (!user || notifications.length === 0) return;
    const archived_at = new Date().toISOString();
    const user_ids = notifications.filter((n) => n.source !== 'global').map((n) => n.id);
    const global_ids = notifications
      .filter((n) => n.source === 'global' && n.global_notification_id)
      .map((n) => n.global_notification_id as string);
    const has_sus = notifications.some((n) => n.type === 'sus_survey');

    await Promise.all([
      user_ids.length > 0
        ? supabase.from('notifications').update({ archived_at }).in('id', user_ids)
        : Promise.resolve(),
      ...global_ids.map((global_notification_id) => upsert_global_action(global_notification_id, { read: true, archived_at })),
    ]);

    set_notifications([]);
    if (has_sus) {
      void supabase.from('profiles').update({ sus_dismissed: true } as Record<string, unknown>).eq('id', user.id);
    }
  };

  const unarchive_notification = async (n: Notification) => {
    if (n.source === 'global' && n.global_notification_id) {
      await upsert_global_action(n.global_notification_id, { read: true, archived_at: null });
    } else {
      await supabase
        .from('notifications')
        .update({ archived_at: null })
        .eq('id', n.id);
    }
    set_archived_notifications((prev) => prev.filter((a) => a.id !== n.id));
    set_notifications((prev) => [...prev, { ...n, archived_at: null }]);
  };

  const mark_all_read = async () => {
    if (!user || unread_count === 0) return;
    const unread_user_ids = notifications.filter((n) => !n.read && n.source !== 'global').map((n) => n.id);
    const unread_global_ids = notifications
      .filter((n) => !n.read && n.source === 'global' && n.global_notification_id)
      .map((n) => n.global_notification_id as string);

    await Promise.all([
      unread_user_ids.length > 0
        ? supabase.from('notifications').update({ read: true }).in('id', unread_user_ids)
        : Promise.resolve(),
      ...unread_global_ids.map((global_notification_id) => upsert_global_action(global_notification_id, { read: true })),
    ]);

    set_notifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  return (
    <>
    <Popover open={open} onOpenChange={set_open}>
      {hidden_trigger ? (
        <PopoverTrigger asChild>
          <span className="sr-only" aria-hidden />
        </PopoverTrigger>
      ) : (
        <Tippy content="Notificacoes" placement="bottom" animateFill plugins={[animateFill]} arrow={false}>
          <span className="inline-flex">
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="relative h-9 w-9 text-muted-foreground" aria-label="Notificacoes">
                <Bell className="h-4 w-4" />
                {unread_count > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground"
                  >
                    {unread_count > 9 ? '9+' : unread_count}
                  </motion.span>
                )}
              </Button>
            </PopoverTrigger>
          </span>
        </Tippy>
      )}
      <PopoverContent
        className="w-[380px] p-0 rounded-2xl border-none shadow-xl bg-card"
        align="end"
        sideOffset={16}
      >
        {/* Cabecalho com botao de arquivadas */}
        <div className="flex items-center gap-1 p-2">
          <div className="relative flex flex-1 items-center gap-1 rounded-xl">
            <button
              onClick={() => set_tab('inbox')}
              className={cn(
                'relative flex-1 rounded-xl px-4 py-2 text-sm font-medium transition-colors',
                tab === 'inbox' ? 'text-foreground bg-accent' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Caixa de entrada
            </button>
          </div>

          <Tippy content="Arquivadas" placement="bottom" animateFill plugins={[animateFill]} arrow={false}>
            <button
              onClick={() => {
                set_tab('archived');
                void fetch_archived();
              }}
              className={cn(
                'relative shrink-0 rounded-xl h-9 w-9 flex items-center justify-center transition-colors',
                tab === 'archived' ? 'text-foreground bg-accent' : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
              )}
            >
              <Archive className="h-4 w-4" />
            </button>
          </Tippy>
        </div>

        <ScrollArea className="h-[400px] overflow-hidden">
          <AnimatePresence mode="wait" initial={false}>
            {tab === 'inbox' ? (
              <motion.div
                key="inbox"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -20, opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="px-2 pb-2"
              >
                {notifications.length > 0 && (
                  <div className="flex items-center justify-between px-2 pb-1">
                    {unread_count > 0 ? (
                      <button
                        onClick={() => void mark_all_read()}
                        className="text-xs text-primary hover:underline"
                      >
                        Marcar todas como lidas
                      </button>
                    ) : <span />}
                    <button
                      onClick={() => void archive_all_notifications()}
                      className="text-xs text-muted-foreground hover:text-foreground hover:underline flex items-center gap-1"
                    >
                      <Archive className="h-3 w-3" />
                      Arquivar todas
                    </button>
                  </div>
                )}
                {loading_inbox ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-2">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                      className="h-5 w-5 rounded-full border-2 border-primary/30 border-t-primary"
                    />
                    <span className="text-xs text-muted-foreground/60">Carregando</span>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="h-12 w-12 rounded-2xl bg-accent/60 flex items-center justify-center mb-3">
                      <Bell className="h-5 w-5 text-muted-foreground/40" />
                    </div>
                    <p className="text-sm font-medium text-muted-foreground/80">Tudo limpo</p>
                    <p className="text-xs text-muted-foreground/50 mt-1">Nenhuma notificacao por aqui</p>
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    {notifications.map((n) => (
                      <SwipeableNotificationCard
                        key={n.id}
                        n={n}
                        on_click={() => {
                          if (n.type === 'sus_survey') {
                            set_open(false);
                            void mark_read(n.id);
                            set_show_sus_survey(true);
                            return;
                          }
                          if (!n.read) void mark_read(n.id);
                          if (n.body || n.image_url) {
                            set_detail_notification(n);
                          }
                        }}
                        on_dismiss={() => {
                          void mark_read(n.id);
                        }}
                        on_archive={() => {
                          void archive_notification(n.id);
                        }}
                      />
                    ))}
                  </div>
                )}
              </motion.div>
            ) : tab === 'archived' ? (
              <motion.div
                key="archived"
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 20, opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="px-2 pb-2"
              >
                {/* Botao para voltar a inbox */}
                <div className="flex items-center gap-2 px-2 pb-2">
                  <button
                    onClick={() => set_tab('inbox')}
                    className="text-xs text-primary hover:underline flex items-center gap-1"
                  >
                    <ArrowCounterClockwise className="h-3 w-3" />
                    Voltar para inbox
                  </button>
                </div>

                {loading_archived ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-2">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                      className="h-5 w-5 rounded-full border-2 border-primary/30 border-t-primary"
                    />
                    <span className="text-xs text-muted-foreground/60">Carregando</span>
                  </div>
                ) : archived_notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="h-12 w-12 rounded-2xl bg-accent/60 flex items-center justify-center mb-3">
                      <Archive className="h-5 w-5 text-muted-foreground/40" />
                    </div>
                    <p className="text-sm font-medium text-muted-foreground/80">Nenhuma arquivada</p>
                    <p className="text-xs text-muted-foreground/50 mt-1">Notificacoes arquivadas aparecem aqui</p>
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    {archived_notifications.map((n) => (
                      <div
                        key={n.id}
                        className="group flex gap-3 rounded-xl px-3 py-3 opacity-60 hover:opacity-90 transition-opacity"
                      >
                        <span className="mt-0.5 shrink-0 flex h-7 w-7 items-center justify-center rounded-lg bg-accent text-muted-foreground">
                          <NotificationIcon type={n.type} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-foreground leading-tight">{n.title}</p>
                          {n.body && <p className="text-xs text-muted-foreground/80 mt-0.5 line-clamp-2">{n.body}</p>}
                          <p className="text-[11px] text-muted-foreground/60 mt-1">{time_ago(n.created_at)}</p>
                        </div>
                        <button
                          onClick={() => void unarchive_notification(n)}
                          className="shrink-0 self-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg p-1.5 hover:bg-accent text-muted-foreground hover:text-foreground"
                          title="Desarquivar"
                        >
                          <ArrowCounterClockwise className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            ) : null}
          </AnimatePresence>
        </ScrollArea>
      </PopoverContent>
    </Popover>

    {user && (
      <SusSurveyModal
        open={show_sus_survey}
        on_open_change={set_show_sus_survey}
        user_id={user.id}
        on_completed={() => {
          void supabase.from('profiles').update({ sus_dismissed: true } as Record<string, unknown>).eq('id', user.id);
          // arquivar a notificacao SUS
          const sus_notif = notifications.find((n) => n.type === 'sus_survey');
          if (sus_notif) {
            void archive_notification(sus_notif.id);
          }
        }}
      />
    )}

    <Dialog open={detail_notification !== null} onOpenChange={(v) => { if (!v) set_detail_notification(null); }}>
      <DialogContent className="max-w-md rounded-2xl p-0 overflow-hidden">
        {detail_notification && (
          <>
            {detail_notification.image_url && (
              /\.(mp4|webm|mov)$/i.test(detail_notification.image_url) ? (
                <video
                  src={detail_notification.image_url}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-52 object-cover"
                />
              ) : (
                <img
                  src={detail_notification.image_url}
                  alt={detail_notification.title}
                  className="w-full h-48 object-cover"
                />
              )
            )}
            <div className="px-6 pt-5 pb-6">
              <DialogHeader>
                <div className="flex items-center gap-2 mb-1">
                  <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                    <NotificationIcon type={detail_notification.type} />
                  </span>
                  <DialogTitle className="text-base font-semibold text-foreground leading-tight">
                    {detail_notification.title}
                  </DialogTitle>
                </div>
              </DialogHeader>
              <p className="text-sm text-muted-foreground leading-relaxed mt-3 whitespace-pre-line">
                {detail_notification.body}
              </p>
              <p className="text-[11px] text-muted-foreground/50 mt-4">
                {time_ago(detail_notification.created_at)}
              </p>
              {detail_notification.link && (
                <Button
                  size="sm"
                  className="mt-4 w-full"
                  onClick={() => {
                    set_detail_notification(null);
                    set_open(false);
                    navigate(detail_notification.link!);
                  }}
                >
                  Abrir
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
